import { useMemo, useRef } from "react";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import { es } from "date-fns/locale";
import { GrayButton, NormalButton } from "@shared/designSystem/Button";
import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import type { DateSelectorEvents } from "@shared/designSystem/DateSelectorEvents";
import { useDateSelectorViewModel } from "@shared/designSystem/DateSelectorViewModel";
import designSystemStyles from "@shared/styles/designSystemStyles";
import styles from "@shared/styles/components/DateSelector.module.css";

interface LocalDateSelectorProps {
    initialDate?: Date | null;
    events?: DateSelectorEvents;
    disableFuture?: boolean;
    disablePast?: boolean;
    minDate?: Date;
    maxDate?: Date;
    clearLabel?: string;
    applyLabel?: string;
    className?: string;
}

const LocalDateSelector: React.FC<LocalDateSelectorProps> = ({
    initialDate,
    events,
    disableFuture = true,
    disablePast = false,
    minDate,
    maxDate,
    clearLabel = "Quitar".localized(),
    applyLabel = "Aplicar".localized(),
    className,
}) => {
    const defaultInitialDateRef = useRef<Date>(new Date());
    const resolvedInitialDate = initialDate === undefined ? defaultInitialDateRef.current : initialDate;

    const {
        selectedDate,
        handleSelectionChange,
        handleApplySelection,
        handleClearSelection,
    } = useDateSelectorViewModel({ initialDate: resolvedInitialDate, events });

    const localeWithCapitalizedMonth = useMemo(() => {
        return {
            ...es,
            localize: {
                ...es.localize,
                month: (month: number, options?: { width?: "narrow" | "abbreviated" | "wide" }) => {
                    const monthName = es.localize.month(month, options);
                    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
                },
            },
        };
    }, []);

    return (
        <VerticalContainer
            className={["DSDateSelector", styles["date-selector"], className].filter(Boolean).join(" ")}
            gap={designSystemStyles.gapSm}
        >
            <div className={styles["calendar-container"]}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeWithCapitalizedMonth}>
                    <DateCalendar
                        value={selectedDate}
                        onChange={(newDate) => handleSelectionChange(newDate)}
                        disableFuture={disableFuture}
                        disablePast={disablePast}
                        minDate={minDate}
                        maxDate={maxDate}
                        showDaysOutsideCurrentMonth
                        fixedWeekNumber={6}
                        dayOfWeekFormatter={(day: string | Date) => {
                            const dayLabel = typeof day === "string"
                                ? day
                                : day.toLocaleDateString("es", { weekday: "short" });
                            const shortDay = dayLabel.replace(".", "").trim().slice(0, 2);
                            return shortDay ? shortDay.charAt(0).toUpperCase() + shortDay.slice(1) : "";
                        }}
                        sx={{
                            width: "100%",
                            "& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root": {
                                color: designSystemStyles.colorMainAction,
                            },
                            "& .MuiPickersCalendarHeader-label": {
                                color: designSystemStyles.colorTitleText,
                                fontWeight: 700,
                            },
                            "& .MuiDayCalendar-weekDayLabel": {
                                color: designSystemStyles.colorTextSecondary,
                                fontWeight: 600,
                            },
                            "& .MuiPickersDay-root": {
                                color: designSystemStyles.colorText,
                            },
                            "& .MuiPickersDay-root.Mui-selected": {
                                backgroundColor: designSystemStyles.colorMainAction,
                                color: "#ffffff",
                            },
                            "& .MuiPickersDay-root.Mui-selected:hover": {
                                backgroundColor: designSystemStyles.colorMainAction,
                            },
                            "& .MuiPickersDay-root:focus.Mui-selected": {
                                backgroundColor: designSystemStyles.colorMainAction,
                            },
                            "& .MuiPickersDay-dayOutsideMonth": {
                                color: designSystemStyles.colorTextInputBorder,
                            },
                        }}
                    />
                </LocalizationProvider>
            </div>

            <HorizontalContainer
                className={styles["actions-container"]}
                justifyContent="center"
                gap={designSystemStyles.gapSm}
            >
                <GrayButton text={clearLabel} onClick={handleClearSelection} />
                <NormalButton text={applyLabel} onClick={handleApplySelection} />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default LocalDateSelector;
